import { LoadingButton } from "@mui/lab";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useFormStatus } from "../inputs/useFormStatus";
import { useFormValidator } from "../inputs/useFormValidator";
import PropTypes from 'prop-types';

const EntityEditor = (props) => {
    const { title, defaultEntity, editing, submitAction, onClose, onSuccess, EditorContent, SuccessContent, dialogProps, hideSubmitButton, onError } = props;

    const [valid, setFieldValidity, resetValid, fieldsValidity, formChanged, onFieldChange] = useFormValidator();// eslint-disable-line no-unused-vars
    const [status, setStatus, statusCodes] = useFormStatus();

    let [entity, setEntity] = useState(defaultEntity);

    useEffect(() => {
        setEntity(defaultEntity);
    }, [defaultEntity]);

    /**
     * 
     * @param {string|Array} idOrArray - A string representing a single field's ID, 
     *                                   or an array of objects representing multiple field updates.
     * @param {*} [value] - The new value for the field (if `idOrArray` is a string).
     * @param {boolean} [valid] - The validity state for the field (if `idOrArray` is a string).
     */
    const onChange = (idOrArray, value, valid) => {
        if (Array.isArray(idOrArray)) {
            const elements = idOrArray;

            let newEntity = {
                ...entity
            };

            for (const element of elements) {
                const { id, value, valid } = element;

                setFieldValidity(id, valid);

                if (entity[id] !== value) {
                    newEntity[id] = value;
                    onFieldChange();
                }
            }

            setEntity(newEntity);
        }
        else {
            const id = idOrArray;

            setFieldValidity(id, valid);

            if (entity[id] !== value) {
                let newEntity = {
                    ...entity
                };
                newEntity[id] = value;
                setEntity(newEntity);
                onFieldChange();
            }
        }
    }

    const onSubmit = async () => {
        if (submitAction) {
            setStatus(statusCodes.SAVING);

            try {
                const newEntity = await submitAction(entity);
                newEntity && setEntity(newEntity);

                if (SuccessContent) {
                    setStatus(statusCodes.DONE);
                }
                else {
                    onSuccess(newEntity);
                }
            }
            catch (error) {
                console.error(error);
                setStatus(statusCodes.AWAITING_USER_INPUT);
                onError(error);
            }
        }
        else {
            onSuccess(entity);
        }
    }

    const submitButtonText = editing ? "Update" : "Create";

    const Actions = () => <DialogActions>
        <Button variant='contained' onClick={onClose} disabled={status === statusCodes.SAVING}>Cancel</Button>
        {!hideSubmitButton && <LoadingButton variant='contained' onClick={onSubmit} disabled={!valid} loading={status === statusCodes.SAVING}>{submitButtonText}</LoadingButton>}
    </DialogActions>

    const Content = useMemo(() => {
        switch (status) {
            case statusCodes.AWAITING_USER_INPUT:
            case statusCodes.SAVING:
                return EditorContent;
            case statusCodes.DONE:
                return SuccessContent;
            default:
                return EditorContent;
        }
    }, [status, EditorContent, SuccessContent, statusCodes]);

    return <>
        <Dialog open {...dialogProps}>
            {status !== statusCodes.DONE && <DialogTitle>{title}</DialogTitle>}
            <DialogContent sx={{ position: 'relative', minHeight: '100px' }}>
                <Content entity={entity} onChange={onChange} onCancel={onClose} disabled={status === statusCodes.SAVING} entityChanged={formChanged} {...props} />
            </DialogContent>
            {status !== statusCodes.DONE && <Actions />}
        </Dialog>
    </>;
}

EntityEditor.propTypes = {
    /**
     * The title displayed at the top of the dialog.
     */
    title: PropTypes.string.isRequired,

    /**
     * The initial entity object used to populate the form fields.
     */
    defaultEntity: PropTypes.object.isRequired,

    /**
     * Indicates if the dialog is in editing mode (true) or creation mode (false).
     */
    editing: PropTypes.bool,

    /**
     * An asynchronous function to handle form submission. 
     * Receives the entity as an argument and should return a promise resolving to the updated entity.
     */
    submitAction: PropTypes.func,

    /**
     * Callback function triggered when the dialog is closed.
     */
    onClose: PropTypes.func.isRequired,

    /**
     * Callback function called after a successful submission.
     * Receives the updated entity as an argument.
     */
    onSuccess: PropTypes.func,

    /**
     * A component rendered as the main content of the form during editing or creation.
     * It receives `entity`, `onChange`, `onCancel`, and `disabled` props.
     */
    EditorContent: PropTypes.elementType.isRequired,

    /**
     * An optional component rendered after a successful submission.
     */
    SuccessContent: PropTypes.elementType,

    /**
     * Additional props to be passed to the MUI `Dialog` component.
     */
    dialogProps: PropTypes.object,
};

export default EntityEditor;